/**
 * `OfflineScreen` — GVW-167 standalone state page.
 *
 * Extended offline state page-level equivalent of GVW-136. Status role.
 *
 * Cite: design/30-engineering/ui-implementation/GVW-167/brief.md
 * Copy: error-catalog.md GV-ERR-OFFLINE, copy-deck.md gvw.167.*
 */

import { useEffect } from 'react';
import { tokens } from '@/design';
import { useLiveRegion } from '@/state/liveRegion';

export function OfflineScreen() {
  const liveRegion = useLiveRegion();

  useEffect(() => {
    // Copy key: gvw.167.title
    document.title = 'You are offline · GeoViewer';
    // Status role per error-catalog.md GV-ERR-OFFLINE
    liveRegion.announce('status', 'You are offline');
  }, [liveRegion]);

  return (
    <main
      role="main"
      aria-labelledby="offline-heading"
      data-gvw-id="GVW-167"
      data-route="/offline"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: tokens.color.surface[0],
        color: tokens.color.ink.primary,
        fontFamily: tokens.font.family.ui,
        padding: tokens.space[5],
        boxSizing: 'border-box',
      }}
    >
      <section
        style={{
          maxWidth: tokens.layout.standaloneStateExtraWide,
          width: '100%',
          background: tokens.color.surface[1],
          border: tokens.border.thin,
          borderLeft: `${tokens.space[1]}px solid ${tokens.color.semantic.warning}`,
          borderRadius: tokens.radius.lg,
          padding: `${tokens.space[6]}px ${tokens.space[5]}px`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: tokens.space[4],
          textAlign: 'center',
        }}
      >
        {/* Hero glyph per brief.md icon plan */}
        <div
          aria-hidden="true"
          style={{
            fontSize: 32,
            color: tokens.color.ink.secondary,
            fontFamily: 'Material Symbols Outlined',
          }}
        >
          cloud_off
        </div>

        {/* Copy key: gvw.167.title from copy-deck.md */}
        <h1
          id="offline-heading"
          style={{
            margin: 0,
            fontSize: tokens.font.size['2xl'],
            lineHeight: tokens.font.lineHeight.tight,
            fontWeight: tokens.font.weight.semibold,
            color: tokens.color.ink.primary,
          }}
        >
          You are offline
        </h1>

        {/* Copy key: gvw.167.body */}
        <p
          style={{
            margin: 0,
            fontSize: tokens.font.size.md,
            lineHeight: tokens.font.lineHeight.normal,
            color: tokens.color.ink.secondary,
            maxWidth: '50ch',
          }}
        >
          Cached layers continue to render. Reconnect to fetch new tiles and save changes.
        </p>

        {/* Recovery action per navigation-map.md §8 */}
        <button
          type="button"
          onClick={() => {
            // Test connectivity by reloading
            window.location.reload();
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: tokens.space[2],
            padding: `${tokens.space[3]}px ${tokens.space[4]}px`,
            background: tokens.color.accent.primary,
            color: tokens.color.accent.on,
            border: 'none',
            borderRadius: tokens.radius.md,
            fontSize: tokens.font.size.md,
            fontWeight: tokens.font.weight.medium,
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              fontFamily: 'Material Symbols Outlined',
              fontSize: 18,
            }}
          >
            refresh
          </span>
          Try again
        </button>

        {/* Network status indicator */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: tokens.space[2],
            fontSize: tokens.font.size.sm,
            color: tokens.color.ink.muted,
            marginTop: tokens.space[2],
          }}
        >
          <span
            aria-hidden="true"
            style={{
              fontFamily: 'Material Symbols Outlined',
              fontSize: 16,
            }}
          >
            wifi_off
          </span>
          Network connection unavailable
        </div>
      </section>
    </main>
  );
}